import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAppConfig } from '../context/AppConfigContext';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Crown, Zap, CreditCard, Clock, CheckCircle, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── PlanCard is defined OUTSIDE Payment so it is never recreated on re-render ──
// If it were inside Payment, every keystroke would recreate the component
// definition, React would unmount+remount it, and the input would lose focus.
const PlanCard = ({
  type, icon: Icon, title, price, original,
  offerActive, offerLabel, features,
  buttonLabel, buttonDisabled, buttonClass,
  showUsd, usdToInr,
  loading, onPayment,
  token,
}) => {
  const [couponCode, setCouponCode]     = useState('');
  const [couponData, setCouponData]     = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  const effectivePrice = couponData ? Math.max(couponData.final_price_inr, 0) : price;

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    try {
      const res = await axios.post(
        `${API}/coupons/validate`,
        { code: couponCode.trim(), type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCouponData(res.data);
      toast.success(`Coupon applied! You save ₹${res.data.discount_amount_inr}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid coupon');
      setCouponData(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponData(null);
    setCouponCode('');
  };

  return (
    <div className={`relative rounded-3xl shadow-xl border-2 overflow-hidden ${
      type === 'premium'
        ? 'bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:bg-slate-800 border-purple-200 dark:border-slate-700'
        : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700'
    }`}>
      {type === 'premium' && (
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full -mr-16 -mt-16" />
      )}
      <div className="relative p-8">

        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl text-white mb-4 ${
          type === 'premium'
            ? 'bg-gradient-to-r from-purple-600 to-pink-600'
            : 'bg-gradient-to-r from-yellow-500 to-orange-500'
        }`}>
          <Icon className="w-6 h-6" />
        </div>

        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{title}</h3>

        {offerActive && offerLabel && (
          <div className={`inline-flex items-center mb-3 px-3 py-1 rounded-full text-sm font-semibold ${
            type === 'premium'
              ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
              : 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
          }`}>
            {offerLabel}
          </div>
        )}

        <div className="flex items-baseline gap-2 mb-1">
          {offerActive && original > price && (
            <span className="text-xl font-semibold text-red-400 line-through">₹{original}</span>
          )}
          <span className="text-4xl font-bold text-gray-900 dark:text-white">
            ₹{effectivePrice}
          </span>
          <span className="text-gray-500 dark:text-slate-400 text-sm">
            /{type === 'premium' ? 'month' : 'credit'}
          </span>
        </div>

        {/* Applied coupon badge */}
        {couponData && (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-semibold mb-2">
            <Tag className="w-3 h-3" />
            {couponData.code} — saved ₹{couponData.discount_amount_inr}
            <button onClick={removeCoupon} className="ml-1 hover:text-red-500 font-bold">×</button>
          </div>
        )}

        {showUsd && usdToInr && (
          <p className="text-xs text-gray-400 dark:text-slate-500 mb-4">
            ≈ ${(effectivePrice / usdToInr).toFixed(2)} USD
          </p>
        )}

        {/* Coupon input — only shown when no coupon applied */}
        {!couponData && (
          <div className="flex gap-2 mb-5">
            <Input
              placeholder="Coupon code"
              value={couponCode}
              onChange={e => setCouponCode(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && validateCoupon()}
              className="text-sm font-mono dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              autoComplete="off"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={validateCoupon}
              disabled={couponLoading || !couponCode.trim()}
              className="shrink-0 border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-300"
            >
              {couponLoading ? '…' : 'Apply'}
            </Button>
          </div>
        )}

        <ul className="space-y-2.5 mb-6">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-700 dark:text-slate-200">{f}</span>
            </li>
          ))}
        </ul>

        <Button
          onClick={() => onPayment(type, couponData?.coupon_id)}
          className={`w-full ${buttonClass}`}
          disabled={loading || buttonDisabled}
        >
          {loading ? 'Processing…' : buttonLabel}
        </Button>
      </div>
    </div>
  );
};

// ── Main Payment page ────────────────────────────────────────────────────────
const Payment = () => {
  const { user, token, setUser } = useAuth();
  const { config } = useAppConfig();

  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usdToInr, setUsdToInr]   = useState(null);

  const premiumPrice    = config.premium_price_inr           ?? 39;
  const promoPrice      = config.promo_price_inr             ?? 1;
  const premiumOriginal = config.premium_original_price_inr  ?? 99;
  const promoOriginal   = config.promo_original_price_inr    ?? 9;
  const offerActive     = config.offer_active                ?? true;
  const showUsd         = config.show_usd                    ?? true;

  useEffect(() => {
    if (showUsd) {
      fetch('https://open.er-api.com/v6/latest/USD')
        .then(r => r.json())
        .then(d => { if (d?.rates?.INR) setUsdToInr(d.rates.INR); })
        .catch(() => {});
    }
    loadPaymentHistory();
  }, [showUsd]);

  const loadPaymentHistory = async () => {
    try {
      const res = await axios.get(`${API}/payment/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentHistory(res.data.payments);
    } catch {}
  };

  const handlePayment = async (type, couponId) => {
    setLoading(true);
    try {
      const payload = { type };
      if (couponId) payload.coupon_id = couponId;

      const orderRes = await axios.post(
        `${API}/payment/create-order`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const script = document.createElement('script');
      script.src   = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        const options = {
          key:       process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount:    orderRes.data.amount,
          currency:  orderRes.data.currency,
          order_id:  orderRes.data.order_id,
          name:      'LitX',
          description: type === 'premium' ? 'Premium Subscription' : 'Post Promotion Credit',
          handler: async (response) => {
            try {
              await axios.post(
                `${API}/payment/verify`,
                {
                  razorpay_order_id:   response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature:  response.razorpay_signature,
                  type,
                },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success('Payment successful! 🎉');
              const userRes = await axios.get(`${API}/profile`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              setUser(userRes.data);
              loadPaymentHistory();
            } catch {
              toast.error('Payment verification failed');
            }
          },
          prefill: { email: user.email, name: user.username },
          theme:   { color: '#06b6d4' },
        };
        new window.Razorpay(options).open();
      };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (ts) =>
    new Date(ts).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8" data-testid="payment-page">
        <div className="max-w-6xl mx-auto">

          <div className="text-center mb-8 page-header-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Payment</h1>
            <p className="text-gray-600 dark:text-slate-400">Upgrade to premium or buy promotion credits</p>
          </div>

          <Tabs defaultValue="plans" className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8 bg-white/60 dark:bg-slate-800/60 backdrop-blur-md border border-gray-200 dark:border-slate-700 rounded-full p-1">
              <TabsTrigger value="plans"   className="rounded-full text-gray-700 dark:text-slate-200 data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-700 dark:data-[state=active]:text-cyan-300">Plans</TabsTrigger>
              <TabsTrigger value="history" className="rounded-full text-gray-700 dark:text-slate-200 data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-700 dark:data-[state=active]:text-cyan-300">History</TabsTrigger>
            </TabsList>

            <TabsContent value="plans">
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                <PlanCard
                  type="premium"
                  icon={Crown}
                  title="Premium"
                  price={premiumPrice}
                  original={premiumOriginal}
                  offerActive={offerActive}
                  offerLabel={config.offer_label_premium}
                  features={[
                    `${config.premium_posts_per_day ?? 15} posts per day`,
                    '6 reports per day',
                    `Save up to ${config.premium_saves_limit ?? 30} posts`,
                    'Auto-promoted posts',
                    'Premium badge',
                    'Glowing profile',
                  ]}
                  buttonLabel={user?.role === 'premium' ? 'Already Premium ✓' : 'Upgrade Now'}
                  buttonDisabled={user?.role === 'premium'}
                  buttonClass="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                  showUsd={showUsd}
                  usdToInr={usdToInr}
                  loading={loading}
                  onPayment={handlePayment}
                  token={token}
                />
                <PlanCard
                  type="promotion"
                  icon={Zap}
                  title="Promotion Credit"
                  price={promoPrice}
                  original={promoOriginal}
                  offerActive={offerActive}
                  offerLabel={config.offer_label_promo}
                  features={[
                    'Promote 1 post',
                    '2 days promotion',
                    'Increased visibility',
                    'Special badge',
                    'Subtle animation',
                  ]}
                  buttonLabel={`Buy Credit · ${user?.credits || 0} owned`}
                  buttonDisabled={false}
                  buttonClass="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
                  showUsd={showUsd}
                  usdToInr={usdToInr}
                  loading={loading}
                  onPayment={handlePayment}
                  token={token}
                />
              </div>
            </TabsContent>

            <TabsContent value="history">
              <div className="max-w-4xl mx-auto">
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-6">
                      <CreditCard className="w-5 h-5 text-cyan-600" />
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Payment History</h3>
                    </div>
                    {paymentHistory.length > 0 ? (
                      <div className="space-y-3">
                        {paymentHistory.map(payment => (
                          <div key={payment.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700 rounded-xl border border-gray-100 dark:border-slate-600">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                payment.type === 'premium' ? 'bg-purple-100 text-purple-600' : 'bg-yellow-100 text-yellow-600'
                              }`}>
                                {payment.type === 'premium' ? <Crown className="w-5 h-5" /> : <Zap className="w-5 h-5" />}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white text-sm">
                                  {payment.type === 'premium' ? 'Premium Subscription' : 'Promotion Credit'}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />{formatDate(payment.created_at)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-gray-900 dark:text-white">₹{(payment.amount / 100).toFixed(2)}</p>
                              {showUsd && usdToInr && (
                                <p className="text-xs text-gray-400">≈ ${(payment.amount / 100 / usdToInr).toFixed(2)}</p>
                              )}
                              <p className="text-xs text-green-600 dark:text-green-400">✓ Completed</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <CreditCard className="w-14 h-14 text-gray-200 dark:text-slate-700 mx-auto mb-3" />
                        <p className="text-gray-500 dark:text-slate-400">No payment history yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Payment;